/* eslint-disable react/prop-types */
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  FiCalendar,
  FiBriefcase,
  FiEdit3,
  FiFileText,
  FiExternalLink,
  FiHome,
  FiImage,
  FiLogOut,
  FiMenu,
  FiPackage,
  FiPlus,
  FiShoppingBag,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { AdminContext } from '../../Context/AdminContext';
import { apiRequest } from '../../Services/api';
import CateringEditor from './CateringEditor';
import CateringOrders from './CateringOrders';
import { easternGreeting, formatEasternDate } from '../../Utils/dateTime';
import './AdminPage.css';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: FiHome },
  { id: 'products', label: 'Products', icon: FiPackage },
  { id: 'events', label: 'Events', icon: FiCalendar },
  { id: 'catering', label: 'Catering', icon: FiBriefcase },
  { id: 'catering-orders', label: 'Catering Orders', icon: FiFileText },
  { id: 'albums', label: 'Albums', icon: FiImage },
  { id: 'orders', label: 'Orders', icon: FiShoppingBag },
];

const EMPTY_PRODUCT = { productName: '', description: '', sizes: [{ size: '16oz', price: '' }] };
const EMPTY_EVENT = { eventTitle: '', date: '', location: '', details: '' };
const ORDER_STATUSES = ['pending', 'confirmed', 'ready', 'completed', 'cancelled'];

function formatDate(value, includeTime = false) {
  if (!value) return '—';
  return formatEasternDate(value, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

function EmptyState({ children }) {
  return <p className='admin-empty'>{children}</p>;
}

function DashboardView({ data, loading, onNavigate }) {
  const summary = data?.summary || { activeProducts: 0, upcomingEvents: 0, openOrders: 0, openCateringOrders: 0 };
  const metrics = [
    ['Active products', summary.activeProducts],
    ['Upcoming events', summary.upcomingEvents],
    ['Open orders', summary.openOrders],
    ['Open catering', summary.openCateringOrders],
  ];

  return (
    <>
      <div className='admin-heading-row'>
        <div>
          <h1>{easternGreeting()}</h1>
          <p>Here&apos;s what needs your attention at the shack.</p>
        </div>
      </div>

      <section className='admin-metrics' aria-label='Business overview'>
        {metrics.map(([label, value]) => (
          <article className='admin-metric' key={label}>
            <span>{label}</span>
            <strong>{loading ? '—' : value}</strong>
          </article>
        ))}
      </section>

      <section className='admin-workspace'>
        <div className='admin-section-heading admin-dashboard-actions'>
          <div>
            <h2>Recent orders</h2>
            <p>The five newest pickup requests.</p>
          </div>
          <div className='admin-action-row'>
            <button className='admin-button admin-button-primary' type='button' onClick={() => onNavigate('products')}>
              <FiPlus /> Add product
            </button>
            <button className='admin-button admin-button-outline' type='button' onClick={() => onNavigate('events')}>
              <FiCalendar /> Publish event
            </button>
          </div>
        </div>
        <OrdersTable orders={data?.recentOrders || []} compact onNavigate={onNavigate} />
      </section>
    </>
  );
}

function OrdersTable({ orders, compact = false, onStatusChange, onNavigate }) {
  if (!orders.length) return <EmptyState>No orders have been placed yet.</EmptyState>;

  return (
    <div className='admin-table-wrap'>
      <table className='admin-table'>
        <thead>
          <tr>
            <th>Order</th>
            <th>Customer</th>
            <th>Pickup</th>
            <th>Status</th>
            <th>Total</th>
            {!compact ? <th>Contact</th> : null}
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td className='admin-order-number'>#{order.id}</td>
              <td>
                <strong>{order.customerName}</strong>
                <span>{formatDate(order.createdAt, true)}</span>
              </td>
              <td>{formatDate(order.pickupDate)}</td>
              <td>
                {onStatusChange ? (
                  <select
                    className={`admin-status admin-status-${order.status}`}
                    value={order.status}
                    onChange={(event) => onStatusChange(order.id, event.target.value)}
                    aria-label={`Status for order ${order.id}`}
                  >
                    {ORDER_STATUSES.map((status) => <option value={status} key={status}>{status}</option>)}
                  </select>
                ) : (
                  <span className={`admin-status-text admin-status-${order.status}`}>{order.status}</span>
                )}
              </td>
              <td>${order.totalAmount}</td>
              {!compact ? <td><a href={`mailto:${order.customerEmail}`}>{order.customerEmail}</a><span>{order.customerPhone}</span></td> : null}
            </tr>
          ))}
        </tbody>
      </table>
      {compact ? <button className='admin-text-button' type='button' onClick={() => onNavigate('orders')}>View all orders →</button> : null}
    </div>
  );
}

function ProductsView({ products, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setEditing(null);
    setForm(EMPTY_PRODUCT);
    setImage(null);
  };

  const editProduct = (product) => {
    setEditing(product.id);
    setImage(null);
    setForm({ productName: product.productName, description: product.description, sizes: product.sizes.map(({ size, price }) => ({ size, price })) });
  };

  const updateSize = (index, field, value) => {
    setForm((current) => ({ ...current, sizes: current.sizes.map((size, sizeIndex) => sizeIndex === index ? { ...size, [field]: value } : size) }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await apiRequest('/products/index.php', { method: 'PUT', body: JSON.stringify({ id: editing, ...form }) });
        if (image) {
          const imageBody = new FormData();
          imageBody.append('id', String(editing));
          imageBody.append('image', image);
          await apiRequest('/products/image.php', { method: 'POST', body: imageBody });
        }
      } else {
        const body = new FormData();
        body.append('productName', form.productName);
        body.append('description', form.description);
        body.append('sizes', JSON.stringify(form.sizes));
        if (image) body.append('image', image);
        await apiRequest('/products/index.php', { method: 'POST', body });
      }
      toast.success(editing ? 'Product updated.' : 'Product added.');
      resetForm();
      await onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this product from the storefront?')) return;
    try {
      await apiRequest(`/products/index.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      toast.success('Product removed.');
      await onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <section className='admin-manage-grid'>
      <div className='admin-workspace'>
        <div className='admin-section-heading'><div><h1>Products</h1><p>Manage the flavors and sizes shown in the shop.</p></div></div>
        <div className='admin-record-list'>
          {products.length ? products.map((product) => (
            <article className='admin-record' key={product.id}>
              <img src={product.imageUrl} alt='' />
              <div><h3>{product.productName}</h3><p>{product.sizes.map((size) => `${size.size} · $${size.price}`).join('  /  ')}</p></div>
              <div className='admin-record-actions'>
                <button type='button' onClick={() => editProduct(product)} aria-label={`Edit ${product.productName}`}><FiEdit3 /></button>
                <button type='button' onClick={() => remove(product.id)} aria-label={`Remove ${product.productName}`}><FiTrash2 /></button>
              </div>
            </article>
          )) : <EmptyState>No active products.</EmptyState>}
        </div>
      </div>
      <form className='admin-form-panel' onSubmit={submit}>
        <div className='admin-section-heading'><div><h2>{editing ? 'Edit product' : 'Add product'}</h2><p>{editing ? 'Update storefront details.' : 'Create a new storefront item.'}</p></div>{editing ? <button className='admin-text-button' type='button' onClick={resetForm}>Cancel</button> : null}</div>
        <label>Product name<input required maxLength='160' value={form.productName} onChange={(event) => setForm({ ...form, productName: event.target.value })} /></label>
        <label>Description<textarea required rows='4' value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
        <fieldset><legend>Sizes and prices</legend>{form.sizes.map((size, index) => (
          <div className='admin-inline-fields' key={`${index}-${size.size}`}>
            <input required aria-label='Size label' placeholder='Size' value={size.size} onChange={(event) => updateSize(index, 'size', event.target.value)} />
            <input required min='0' step='0.01' type='number' aria-label='Price' placeholder='Price' value={size.price} onChange={(event) => updateSize(index, 'price', event.target.value)} />
            {form.sizes.length > 1 ? <button type='button' aria-label='Remove size' onClick={() => setForm({ ...form, sizes: form.sizes.filter((_, sizeIndex) => sizeIndex !== index) })}><FiX /></button> : null}
          </div>
        ))}<button className='admin-text-button' type='button' onClick={() => setForm({ ...form, sizes: [...form.sizes, { size: '', price: '' }] })}>+ Add another size</button></fieldset>
        <label>Product image <span>{editing ? 'Optional replacement' : 'Optional'}</span><input type='file' accept='image/jpeg,image/png,image/webp' onChange={(event) => setImage(event.target.files?.[0] || null)} /></label>
        <button className='admin-button admin-button-primary' type='submit' disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Add product'}</button>
      </form>
    </section>
  );
}

function EventsView({ events, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);

  const reset = () => { setEditing(null); setForm(EMPTY_EVENT); };
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await apiRequest('/events/index.php', { method: editing ? 'PUT' : 'POST', body: JSON.stringify(editing ? { id: editing, ...form } : form) });
      toast.success(editing ? 'Event updated.' : 'Event published.');
      reset();
      await onRefresh();
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await apiRequest(`/events/index.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' }); await onRefresh(); toast.success('Event deleted.'); } catch (error) { toast.error(error.message); }
  };

  return (
    <section className='admin-manage-grid'>
      <div className='admin-workspace'>
        <div className='admin-section-heading'><div><h1>Events</h1><p>Keep the public calendar accurate and current.</p></div></div>
        <div className='admin-record-list'>{events.length ? events.map((item) => (
          <article className='admin-record' key={item.id}><div className='admin-date-block'>{formatDate(item.date)}</div><div><h3>{item.eventTitle}</h3><p>{item.location}</p></div><div className='admin-record-actions'><button type='button' onClick={() => { setEditing(item.id); setForm({ eventTitle: item.eventTitle, date: item.date, location: item.location, details: item.details }); }} aria-label={`Edit ${item.eventTitle}`}><FiEdit3 /></button><button type='button' onClick={() => remove(item.id)} aria-label={`Delete ${item.eventTitle}`}><FiTrash2 /></button></div></article>
        )) : <EmptyState>No events are scheduled.</EmptyState>}</div>
      </div>
      <form className='admin-form-panel' onSubmit={submit}>
        <div className='admin-section-heading'><div><h2>{editing ? 'Edit event' : 'Publish event'}</h2><p>Tell customers where to find you next.</p></div>{editing ? <button className='admin-text-button' type='button' onClick={reset}>Cancel</button> : null}</div>
        <label>Event name<input required maxLength='160' value={form.eventTitle} onChange={(event) => setForm({ ...form, eventTitle: event.target.value })} /></label>
        <label>Date<input required type='date' value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} /></label>
        <label>Location<input required maxLength='255' value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} /></label>
        <label>Details<textarea required rows='5' value={form.details} onChange={(event) => setForm({ ...form, details: event.target.value })} /></label>
        <button className='admin-button admin-button-primary' type='submit' disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Publish event'}</button>
      </form>
    </section>
  );
}

function AlbumsView({ albums, onRefresh }) {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(null);
  const submit = async (event) => {
    event.preventDefault();
    try {
      await apiRequest('/albums/index.php', { method: editing ? 'PUT' : 'POST', body: JSON.stringify(editing ? { id: editing, name } : { name }) });
      toast.success(editing ? 'Album renamed.' : 'Album created.');
      setName(''); setEditing(null); await onRefresh();
    } catch (error) { toast.error(error.message); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this empty album?')) return;
    try { await apiRequest(`/albums/index.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' }); await onRefresh(); toast.success('Album deleted.'); } catch (error) { toast.error(error.message); }
  };
  return (
    <section className='admin-workspace'>
      <div className='admin-section-heading'><div><h1>Albums</h1><p>Organize photo collections for future storefront stories.</p></div></div>
      <form className='admin-album-form' onSubmit={submit}><label><span>Album name</span><input required maxLength='160' value={name} onChange={(event) => setName(event.target.value)} placeholder='Fall markets' /></label><button className='admin-button admin-button-primary' type='submit'>{editing ? 'Save name' : 'Create album'}</button>{editing ? <button className='admin-button admin-button-outline' type='button' onClick={() => { setEditing(null); setName(''); }}>Cancel</button> : null}</form>
      <div className='admin-record-list'>{albums.length ? albums.map((album) => <article className='admin-record' key={album.id}><div className='admin-album-mark'><FiImage /></div><div><h3>{album.name}</h3><p>Created {formatDate(album.createdAt)}</p></div><div className='admin-record-actions'><button type='button' onClick={() => { setEditing(album.id); setName(album.name); }} aria-label={`Rename ${album.name}`}><FiEdit3 /></button><button type='button' onClick={() => remove(album.id)} aria-label={`Delete ${album.name}`}><FiTrash2 /></button></div></article>) : <EmptyState>No albums created yet.</EmptyState>}</div>
    </section>
  );
}

export default function AdminPage() {
  const { user, canAdmin, authLoading, logout } = useContext(AdminContext);
  const [section, setSection] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [records, setRecords] = useState({ products: [], events: [], catering: null, cateringOrders: null, albums: [], orders: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const priorTitle = document.title;
    const robots = document.head.querySelector('meta[name="robots"]');
    const priorRobots = robots?.getAttribute('content');
    document.title = "Admin Dashboard | Aunt Jack's Peanut Shack";
    robots?.setAttribute('content', 'noindex,nofollow');
    return () => {
      document.title = priorTitle;
      if (priorRobots) robots?.setAttribute('content', priorRobots);
    };
  }, []);

  const loadSection = useCallback(async (selected) => {
    setLoading(true);
    try {
      const paths = { dashboard: '/admin/dashboard.php', products: '/products/index.php', events: '/events/index.php', catering: '/catering/index.php', 'catering-orders': '/admin/catering-orders.php', albums: '/albums/index.php', orders: '/admin/orders.php' };
      const data = await apiRequest(paths[selected]);
      if (selected === 'dashboard') setDashboard(data);
      else if (selected === 'catering-orders') setRecords((current) => ({ ...current, cateringOrders: data }));
      else setRecords((current) => ({ ...current, [selected]: data[selected] || [] }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && canAdmin) loadSection(section);
  }, [authLoading, canAdmin, section, loadSection]);

  if (authLoading) return <div className='admin-gate'>Checking administrator access…</div>;
  if (!user) return <Navigate to='/auth' replace state={{ from: '/admin' }} />;
  if (!canAdmin) return <Navigate to='/profile' replace />;

  const chooseSection = (nextSection) => { setSection(nextSection); setMenuOpen(false); };
  const handleLogout = async () => { await logout(); navigate('/'); };
  const updateOrderStatus = async (id, status) => {
    try {
      await apiRequest('/admin/orders.php', { method: 'PATCH', body: JSON.stringify({ id, status }) });
      setRecords((current) => ({ ...current, orders: current.orders.map((order) => order.id === id ? { ...order, status } : order) }));
      toast.success(`Order #${id} marked ${status}.`);
    } catch (error) { toast.error(error.message); }
  };

  return (
    <div className='admin-shell'>
      <aside className={`admin-sidebar ${menuOpen ? 'is-open' : ''}`}>
        <div className='admin-brand'><strong>Aunt Jack&apos;s Admin</strong><span>Aunt Jack&apos;s Peanut Shack</span></div>
        <nav aria-label='Admin navigation'>
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => <button type='button' className={section === id ? 'is-active' : ''} onClick={() => chooseSection(id)} key={id}><Icon /><span>{label}</span></button>)}
        </nav>
        <div className='admin-sidebar-footer'>
          <Link to='/'><FiExternalLink /> View storefront</Link>
          <button type='button' onClick={handleLogout}><FiLogOut /> Sign out</button>
        </div>
      </aside>
      {menuOpen ? <button type='button' className='admin-scrim' aria-label='Close navigation' onClick={() => setMenuOpen(false)} /> : null}
      <main className='admin-main'>
        <header className='admin-topbar'>
          <button type='button' className='admin-menu-button' aria-label={menuOpen ? 'Close navigation' : 'Open navigation'} onClick={() => setMenuOpen((current) => !current)}>{menuOpen ? <FiX /> : <FiMenu />}</button>
          <div className='admin-user'><span>{user.displayName?.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'AJ'}</span><div><strong>{user.displayName}</strong><small>Administrator</small></div></div>
        </header>
        <div className='admin-content'>
          {section === 'dashboard' ? <DashboardView data={dashboard} loading={loading} onNavigate={chooseSection} /> : null}
          {section === 'products' ? <ProductsView products={records.products} onRefresh={() => loadSection('products')} /> : null}
          {section === 'events' ? <EventsView events={records.events} onRefresh={() => loadSection('events')} /> : null}
          {section === 'catering' ? <CateringEditor content={records.catering} loading={loading} onSaved={(catering) => setRecords((current) => ({ ...current, catering }))} /> : null}
          {section === 'catering-orders' ? <CateringOrders data={records.cateringOrders} loading={loading} onRefresh={() => loadSection('catering-orders')} /> : null}
          {section === 'albums' ? <AlbumsView albums={records.albums} onRefresh={() => loadSection('albums')} /> : null}
          {section === 'orders' ? <section className='admin-workspace'><div className='admin-section-heading'><div><h1>Orders</h1><p>Review pickup requests and keep their status current.</p></div></div><OrdersTable orders={records.orders} onStatusChange={updateOrderStatus} /></section> : null}
        </div>
      </main>
    </div>
  );
}
